/**
 * Service de mapping des données frontend
 * Standardise les conversions IRI ↔ ID et harmonise les formats
 */

/**
 * Convertit une IRI en ID
 * @param {string} iri - IRI à convertir (format: /api/entity/123)
 * @returns {number|null} ID extrait ou null
 */
export function iriToId(iri) {
  if (!iri || typeof iri !== 'string') {
    return null;
  }

  // Extraire l'ID de l'IRI (format: /api/entity/123)
  const match = iri.match(/\/api\/\w+\/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Convertit un ID en IRI
 * @param {number} id - ID à convertir
 * @param {string} entityType - Type d'entité (clients, contacts, etc.)
 * @returns {string} IRI générée
 */
export function idToIri(id, entityType) {
  if (!id || !entityType) {
    return null;
  }
  return `/api/${entityType}/${id}`;
}

/**
 * Convertit une liste d'IRI en IDs
 * @param {string[]} iris - Liste d'IRI
 * @returns {number[]} Liste d'IDs
 */
export function irisToIds(iris) {
  if (!Array.isArray(iris)) {
    return [];
  }
  return iris.map(iri => iriToId(iri)).filter(id => id !== null);
}

/**
 * Convertit une liste d'IDs en IRI
 * @param {number[]} ids - Liste d'IDs
 * @param {string} entityType - Type d'entité
 * @returns {string[]} Liste d'IRI
 */
export function idsToIris(ids, entityType) {
  if (!Array.isArray(ids) || !entityType) {
    return [];
  }
  return ids.map(id => idToIri(id, entityType)).filter(iri => iri !== null);
}

/**
 * Normalise les données d'un ticket pour l'affichage
 * @param {Object} ticket - Données du ticket
 * @returns {Object} Ticket normalisé
 */
export function normalizeTicketForUI(ticket) {
  if (!ticket) {
    return null;
  }

  return {
    ...ticket,
    // Conversion des IRI en IDs pour l'affichage
    clientId: iriToId(ticket.clientId),
    contactId: iriToId(ticket.contactId),
    collaborateurId: iriToId(ticket.collaborateurId),
    statutId: iriToId(ticket.statutId),
    // Données enrichies si disponibles
    client: ticket.client || null,
    contact: ticket.contact || null,
    collaborateur: ticket.collaborateur || null,
    statut: ticket.statut || null
  };
}

/**
 * Normalise les données d'un client pour l'affichage
 * @param {Object} client - Données du client
 * @returns {Object} Client normalisé
 */
export function normalizeClientForUI(client) {
  if (!client) {
    return null;
  }

  return {
    ...client,
    // Conversion des IRI en IDs
    contacts: client.contacts ? client.contacts.map(contact => normalizeContactForUI(contact)) : []
  };
}

/**
 * Normalise les données d'un contact pour l'affichage
 * @param {Object} contact - Données du contact
 * @returns {Object} Contact normalisé
 */
export function normalizeContactForUI(contact) {
  if (!contact) {
    return null;
  }

  return {
    ...contact,
    // Conversion des IRI en IDs
    clientId: iriToId(contact.clientId),
    client: contact.client || null
  };
}

/**
 * Normalise les données d'un collaborateur pour l'affichage
 * @param {Object} collaborateur - Données du collaborateur
 * @returns {Object} Collaborateur normalisé
 */
export function normalizeCollaborateurForUI(collaborateur) {
  if (!collaborateur) {
    return null;
  }

  return {
    ...collaborateur,
    // Conversion des IRI en IDs
    roleId: iriToId(collaborateur.roleId),
    role: collaborateur.role || null
  };
}

/**
 * Normalise les données d'un statut pour l'affichage
 * @param {Object} statut - Données du statut
 * @returns {Object} Statut normalisé
 */
export function normalizeStatutForUI(statut) {
  if (!statut) {
    return null;
  }

  return {
    ...statut
  };
}

/**
 * Convertit les données pour l'envoi à l'API
 * @param {Object} data - Données à convertir
 * @param {string} entityType - Type d'entité
 * @returns {Object} Données converties pour l'API
 */
export function denormalizeForAPI(data, entityType) {
  if (!data || !entityType) {
    return data;
  }

  const converted = { ...data };

  // Conversion des IDs en IRI selon le type d'entité
  const idFields = {
    'ticket': ['clientId', 'contactId', 'collaborateurId', 'statutId'],
    'contact': ['clientId'],
    'collaborateur': ['roleId']
  };

  const fieldsToConvert = idFields[entityType] || [];
  
  fieldsToConvert.forEach(field => {
    if (converted[field] && typeof converted[field] === 'number') {
      const entityTypeForField = getEntityTypeForField(field);
      converted[field] = idToIri(converted[field], entityTypeForField);
    }
  });

  return converted;
}

/**
 * Obtient le type d'entité pour un champ donné
 * @param {string} field - Nom du champ
 * @returns {string} Type d'entité
 */
function getEntityTypeForField(field) {
  const fieldMapping = {
    'clientId': 'clients',
    'contactId': 'contacts',
    'collaborateurId': 'collaborateurs',
    'statutId': 'statuts',
    'roleId': 'roles'
  };

  return fieldMapping[field] || 'unknown';
}

/**
 * Normalise une liste d'entités pour l'affichage
 * @param {Array} entities - Liste d'entités
 * @param {string} entityType - Type d'entité
 * @returns {Array} Liste normalisée
 */
export function normalizeEntityListForUI(entities, entityType) {
  if (!Array.isArray(entities)) {
    return [];
  }

  const normalizer = {
    'ticket': normalizeTicketForUI,
    'client': normalizeClientForUI,
    'contact': normalizeContactForUI,
    'collaborateur': normalizeCollaborateurForUI,
    'statut': normalizeStatutForUI
  };

  const normalizeFunction = normalizer[entityType] || (entity => entity);
  return entities.map(normalizeFunction);
}

/**
 * Crée un mapping ID ↔ libellé pour les listes déroulantes
 * @param {Array} entities - Liste d'entités
 * @param {string} idField - Champ ID
 * @param {string} labelField - Champ libellé
 * @returns {Object} Mapping ID → libellé
 */
export function createIdLabelMapping(entities, idField = 'id', labelField = 'libelle') {
  if (!Array.isArray(entities)) {
    return {};
  }

  return entities.reduce((mapping, entity) => {
    const id = entity[idField];
    const label = entity[labelField];
    if (id !== undefined && label !== undefined) {
      mapping[String(id)] = label;
    }
    return mapping;
  }, {});
}

/**
 * Crée un mapping IRI ↔ libellé pour les listes déroulantes
 * @param {Array} entities - Liste d'entités
 * @param {string} iriField - Champ IRI
 * @param {string} labelField - Champ libellé
 * @returns {Object} Mapping IRI → libellé
 */
export function createIriLabelMapping(entities, iriField = 'id', labelField = 'libelle') {
  if (!Array.isArray(entities)) {
    return {};
  }

  return entities.reduce((mapping, entity) => {
    const iri = entity[iriField];
    const label = entity[labelField];
    if (iri && label) {
      mapping[iri] = label;
    }
    return mapping;
  }, {});
}

/**
 * Filtre les entités par relation
 * @param {Array} entities - Liste d'entités à filtrer
 * @param {string} relationField - Champ de relation
 * @param {string|number} relationValue - Valeur de relation
 * @returns {Array} Entités filtrées
 */
export function filterEntitiesByRelation(entities, relationField, relationValue) {
  if (!Array.isArray(entities)) {
    return [];
  }

  return entities.filter(entity => {
    const entityValue = entity[relationField];
    
    // Gérer les IRI
    if (typeof entityValue === 'string' && entityValue.includes('/api/')) {
      const entityId = iriToId(entityValue);
      return entityId === relationValue;
    }
    
    // Gérer les IDs directs
    return entityValue === relationValue;
  });
}

/**
 * Valide la cohérence des données avant envoi
 * @param {Object} data - Données à valider
 * @param {string} entityType - Type d'entité
 * @returns {Array} Liste des erreurs
 */
export function validateDataConsistency(data, entityType) {
  const errors = [];

  if (!data) {
    errors.push('Données manquantes');
    return errors;
  }

  // Validation spécifique par type d'entité
  switch (entityType) {
    case 'ticket':
      if (data.clientId && data.contactId) {
        // Vérifier que le contact appartient au client
        // Cette validation devrait être faite côté backend
        // mais on peut faire une vérification basique côté frontend
      }
      break;
    
    case 'contact':
      if (!data.clientId) {
        errors.push('Un contact doit avoir un client');
      }
      break;
  }

  return errors;
}

/**
 * Export par défaut avec toutes les fonctions
 */
export default {
  iriToId,
  idToIri,
  irisToIds,
  idsToIris,
  normalizeTicketForUI,
  normalizeClientForUI,
  normalizeContactForUI,
  normalizeCollaborateurForUI,
  normalizeStatutForUI,
  denormalizeForAPI,
  normalizeEntityListForUI,
  createIdLabelMapping,
  createIriLabelMapping,
  filterEntitiesByRelation,
  validateDataConsistency
};
